import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useCollection } from '../hooks/useCollections';
import { useCollectionListings } from '../hooks/useListings';
import CollectionAudienceActions from '../components/CollectionAudienceActions';
import DeleteCollectionDialog from '../components/DeleteCollectionDialog';
import ListingCard from '../components/ListingCard';
import GroupLinkDialog from '../components/GroupLinkDialog';
import ReportDialog from '../components/ReportDialog';
import UserAvatar from '../components/UserAvatar';
import { EmptyState, LoadingState, StateForError } from '../components/PageStates';
import type { Collection } from '../domain/collections';
import { useRepositories } from '../hooks/useRepositories';
import { getApiErrorMessage } from '../utils/errors';
import { fullName } from '../utils/people';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Flag, Inbox, Link2, Lock, Package, Plus, Star, Trash2 } from 'lucide-react';
import { ErrorText } from '../components/ErrorText';

export default function CollectionDetail() {
  const { id = '' } = useParams<{ id: string }>();
  const collection = useCollection(id);
  // The collection as an action on this page left it (e.g. made private).
  const [changed, setChanged] = useState<Collection | null>(null);

  if (collection.status === 'loading') return <LoadingState label="Loading collection" />;
  if (collection.status === 'error') {
    return <StateForError error={collection.error} onRetry={collection.reload} />;
  }
  const current = changed?.id === id ? changed : collection.data;
  return <CollectionView collection={current} onChanged={setChanged} />;
}

function CollectionView({
  collection,
  onChanged,
}: {
  collection: Collection;
  onChanged: (collection: Collection) => void;
}) {
  const listings = useCollectionListings(collection.id);
  const navigate = useNavigate();
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [reporting, setReporting] = useState(false);
  const mine = collection.myRelation === 'owner';
  const count = collection.listingCount;

  return (
    <div className="space-y-10">
      <section className="rounded-3xl bg-card p-8 md:p-10">
        <div className="mb-5 flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-wood/15 px-3 py-1 text-xs font-bold uppercase tracking-wider text-wood">
            Collection
          </span>
          {collection.isDefault && (
            <span className="inline-flex items-center gap-1 rounded-full bg-muted px-3 py-1 text-xs font-semibold text-muted-foreground">
              <Star className="size-3" /> Default
            </span>
          )}
          {collection.visibility === 'private' && (
            <span className="inline-flex items-center gap-1 rounded-full bg-muted px-3 py-1 text-xs font-semibold text-muted-foreground">
              <Lock className="size-3" /> Private
            </span>
          )}
        </div>

        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div className="flex-1">
            <h1 className="text-4xl font-bold leading-[1.05] tracking-tight text-foreground md:text-5xl">
              {collection.name}
            </h1>
            {collection.description && (
              <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground">
                {collection.description}
              </p>
            )}
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-2 rounded-full bg-muted px-4 py-2 text-sm font-semibold text-foreground">
                <UserAvatar
                  person={collection.owner}
                  className="size-5"
                  textClassName="text-[9px]"
                />
                By {fullName(collection.owner)}
              </span>
              <span className="inline-flex items-center gap-2 rounded-full bg-muted px-4 py-2 text-sm font-semibold text-foreground">
                <Package className="size-4 text-muted-foreground" />
                {count} {count === 1 ? 'listing' : 'listings'}
              </span>
            </div>
          </div>

          {mine && (
            <div className="flex flex-shrink-0 flex-wrap gap-3">
              {collection.visibility === 'private' && (
                <Button
                  variant="secondary"
                  className="rounded-full"
                  onClick={() => setLinkDialogOpen(true)}
                >
                  <Link2 />
                  Make a group link
                </Button>
              )}
              <Button className="rounded-full" asChild>
                <Link to={`/collections/${collection.id}/listings/create`}>
                  <Plus />
                  Add listing
                </Link>
              </Button>
            </div>
          )}
        </div>

        <CollectionAudienceActions
          key={collection.visibility}
          collection={collection}
          onChanged={onChanged}
        />

        {mine && <CommentsToggle collection={collection} />}

        {!mine && (
          <div className="mt-8">
            <Button
              variant="ghost"
              className="rounded-full text-muted-foreground"
              aria-label="Report this collection"
              onClick={() => setReporting(true)}
            >
              <Flag />
              Report
            </Button>
            <ReportDialog
              target={{ kind: 'collection', id: collection.id }}
              open={reporting}
              onOpenChange={setReporting}
            />
          </div>
        )}

        {mine && !collection.isDefault && (
          <div className="mt-8">
            <Button
              variant="ghost"
              className="rounded-full text-destructive"
              onClick={() => setDeleting(true)}
            >
              <Trash2 />
              Delete collection
            </Button>
            <DeleteCollectionDialog
              collectionId={collection.id}
              open={deleting}
              onOpenChange={setDeleting}
              onDeleted={() => navigate('/my-collections')}
            />
          </div>
        )}
      </section>

      <section aria-labelledby="collection-listings">
        <h2
          id="collection-listings"
          className="mb-5 text-2xl font-bold tracking-tight text-foreground"
        >
          Listings
        </h2>
        {listings.status === 'loading' ? (
          <LoadingState label="Loading listings" />
        ) : listings.status === 'error' ? (
          <StateForError error={listings.error} onRetry={listings.reload} />
        ) : listings.data.length === 0 ? (
          <EmptyState
            icon={<Inbox className="size-8" />}
            title="No listings here yet"
            description="Listings posted to this collection show up here."
            action={
              mine ? (
                <Button className="rounded-full" asChild>
                  <Link to={`/collections/${collection.id}/listings/create`}>
                    <Plus />
                    Add listing
                  </Link>
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {listings.data.map((listing) => (
              <ListingCard key={listing.id} listing={listing} />
            ))}
          </div>
        )}
      </section>

      {mine && collection.visibility === 'private' && (
        <GroupLinkDialog
          open={linkDialogOpen}
          onOpenChange={setLinkDialogOpen}
          preselectedCollectionId={collection.id}
        />
      )}
    </div>
  );
}

/** Owner's comments on/off, saved at once and reverted if the save fails. */
function CommentsToggle({ collection }: { collection: Collection }) {
  const { collections } = useRepositories();
  const [enabled, setEnabled] = useState(collection.commentsEnabled);
  const [error, setError] = useState<string | null>(null);

  const toggle = async (next: boolean) => {
    const previous = enabled;
    setEnabled(next);
    setError(null);
    try {
      await collections.update(collection.id, { commentsEnabled: next });
    } catch (err) {
      setEnabled(previous);
      setError(getApiErrorMessage(err, 'Could not update comments.'));
    }
  };

  return (
    <div className="mt-8 flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <Checkbox
          id="comments_enabled"
          checked={enabled}
          onCheckedChange={(checked) => toggle(checked === true)}
        />
        <Label htmlFor="comments_enabled" className="cursor-pointer">
          Allow comments
        </Label>
      </div>
      <p className="text-xs text-muted-foreground">
        When off, existing comments are hidden but not deleted.
      </p>
      {error && <ErrorText className="text-xs">{error}</ErrorText>}
    </div>
  );
}
